/**
 * Google Maps deep-link construction - pure URL building, no API calls.
 *
 * The itinerary detail page hands each day's ordered stops to
 * `buildDirectionsUrls` and renders "Open route in Google Maps" links.
 * The Maps directions URL format caps waypoints, so long days split
 * into consecutive legs (each leg starts where the previous ended).
 */

/**
 * A routable point. COORDINATES win when we have them: they came from
 * Google's own geocoder at generation time, so the route always resolves -
 * free-text addresses can be unfindable (or, worse, re-interpreted: a bare
 * "jfk" once landed a route in Adelaide). Address text is the fallback for
 * points that were never geocoded.
 */
export interface MapPoint {
  address: string
  lat?: number | null
  lng?: number | null
}

/**
 * Maps' /dir/ deep link supports origin + destination + 9 waypoints
 * comfortably across platforms - so at most 11 points per link.
 */
const MAX_POINTS_PER_LINK = 11

function encodePoint(point: MapPoint): string {
  if (point.lat != null && point.lng != null) {
    return `${point.lat},${point.lng}`
  }
  if (point.address) {
    return encodeURIComponent(point.address)
  }
  return ''
}

function buildLink(points: MapPoint[]): string {
  const origin = encodePoint(points[0])
  const destination = encodePoint(points[points.length - 1])
  const waypoints = points
    .slice(1, -1)
    .map(encodePoint)
    .join('%7C') // '|' separator, pre-encoded
  const base = 'https://www.google.com/maps/dir/?api=1'
  const wp = waypoints ? `&waypoints=${waypoints}` : ''
  return `${base}&origin=${origin}&destination=${destination}${wp}&travelmode=driving`
}

/**
 * Build one or more Google Maps directions links visiting `stops` in
 * order, optionally bracketed by `origin` and `end` points (airport,
 * office - ideally already geocoded so they carry lat/lng). Days longer
 * than the per-link point cap split into consecutive legs; each
 * subsequent leg starts at the previous leg's last stop so the drive is
 * continuous.
 */
export function buildDirectionsUrls(
  stops: MapPoint[],
  origin?: MapPoint,
  end?: MapPoint,
): string[] {
  const usable = (p: MapPoint | undefined): p is MapPoint =>
    !!p && (!!p.address || (p.lat != null && p.lng != null))
  const points: MapPoint[] = [
    ...(usable(origin) ? [origin] : []),
    ...stops.filter(usable),
    ...(usable(end) ? [end] : []),
  ]
  if (points.length < 2) return []

  const urls: string[] = []
  let start = 0
  while (start < points.length - 1) {
    // Overlap by one point so leg N+1 begins where leg N ended.
    const end = Math.min(start + MAX_POINTS_PER_LINK, points.length)
    urls.push(buildLink(points.slice(start, end)))
    if (end >= points.length) break
    start = end - 1
  }
  return urls
}
